/**
 * @author Karthik <karthik.x@314ecorp.com>
 * @description Menu config
 */

import React from 'react';
import _ from 'lodash';
import { AreaChartOutlined, ExportOutlined, LinkOutlined } from '@ant-design/icons';
import { Broadcast, Home, SettingTwo, ViewGridList, Peoples, Comment, Notepad, Pic, MusicMenu, LayoutOne } from '@icon-park/react';
import { Icon, IIconProps } from '@icon-park/react/lib/runtime';
import { MenuDataItem } from '@ant-design/pro-components';
import { generatePath, PathMatch, Location } from 'react-router-dom';

import ChatIcon from '@admin-vite/features/home/components/chat/ChatIcon';
import InteractiveSideBarIcon from '@admin-vite/features/edit/icons/InteractiveSideBarIcon';
import NonInteractiveSidebarIcon from '@admin-vite/features/edit/icons/NonInteractiveSidebarIcon';
import TranscriptSideBarIcon from '@admin-vite/features/edit/icons/TranscriptSideBarIcon';
import config from '@admin-vite/config';
import { FEATURES_ROLES, TAllRoles } from './rolesJeeves';
import {
	AISegmentationProcessingPage,
	AIVideoGenerationProcessingPage,
	AiSearchPageWithAuth,
	AnalyticsPageWithAuth,
	AssetPreviewPageWithAuth,
	AssetsPageWithAuth,
	AssignmentsPageWithAuth,
	BroadcastPageWithAuth,
	EditPageWithAuth,
	HomePageWithAuth,
	ModeSelectionPage,
	SystemStatusPageWithAuth,
	UserListPageWithAuth,
} from '@admin-vite/pages/imports';

export const menuKeys = {
	home: 'Home',
	assets: 'Assets',
	asset: 'asset',
	analytics: 'Analytics',
	assignments: 'Assignments',
	createTipsheet: 'Create Tip Sheet',
	createDocument: 'Create Document',
	recorder: 'Recorder',
	broadcasts: 'Broadcasts',
	userLists: 'User Lists',
	systemStatus: 'System Status',
	edit: 'Edit',
	transcript: 'Transcript',
	interactiveElements: 'Interactive Elements',
	templates: 'Templates',
	nonInteractiveElements: 'Non Interactive Elements',
	nonInteractiveTemplates: 'Non Interactive Templates',
	aiVoiceNarration: 'AI Voice Narration',
	reviewComments: 'Review Comments',
	help: 'Help',
	assetPreprocessing: 'Asset Preprocessing',
	archived: 'Archived',
	myAssignments: 'My Assignments',
	myAssignmentsPreview: 'My Assignments Preview',
	configuration: 'Configuration',
	chat: 'Chat',
	modeSelection: 'Mode Selection',
	aiSegmentationProcessing: 'AI Segmentation Processing',
	aiVideoGenerationProcessing: 'AI Video Generation Processing',
	assetReferencing: 'Asset Referencing',
	aiSearch: 'AI Search',
} as const;

export const featuresToShow = [
	{ key: menuKeys.broadcasts, label: menuKeys.broadcasts, value: menuKeys.broadcasts },
	{ key: menuKeys.userLists, label: menuKeys.userLists, value: menuKeys.userLists },
	{ key: menuKeys.help, label: menuKeys.help, value: menuKeys.help },
];

export type TMenuKeys = (typeof menuKeys)[keyof typeof menuKeys];

export const routes = {
	// --- Home screen routes ---
	// Home
	root: '/*',

	// View asset routes - Learners
	legacySearchAsset: '/search-asset',
	learnerAssetPreview: '/content/*',
	assetFromID: '/content/:id',
	assetFromSearchTerm: '/content/:id/:searchTerm',

	// My Assignments
	myAssignments: '/my-assignments',
	completedAssignments: '/my-assignments/completed',
	inProgressAssignments: '/my-assignments/incomplete',
	myAssignmentsByAssignmentId: '/my-assignments/:assignmentId',
	myAssignmentsByAssignmentIdAndBatchId: '/my-assignments/:assignmentId/:batchId',
	myAssignmentsPreview: '/my-assignments/:assignmentId/:batchId/:itemId',

	meetings: '/meeting',
	player: '/player/:tokens',
	embedded: '/embedded/:id',
	addAsset: '/add-asset/*',
	analytics: '/analytics/*',
	assetArchived: '/assets/archived',
	assetsPublished: '/assets/published',
	assetsUnpublished: '/assets/unpublished',
	assets: '/assets/*',
	assignments: '/assignments/*',
	authorAssetPreview: '/assets/:id/preview',
	broadcasts: '/broadcasts/*',
	createAssignment: '/create-assignment',
	createTipsheet: '/create-tipsheet',
	createDocument: '/create-document',
	duplicateAssignment: '/duplicate-assignment/:duplicateFrom',
	edit: '/assets/:streamID/edit/*',
	editAssignment: '/edit-assignment/:editId',
	explore: '/explore/*',
	resources: '/resources/*',
	searchResults: '/search-results/*',
	systemStatus: '/system-status/*',
	userLists: '/user-lists/*',
	assetsBulkPublish: '/bulk-publish',
	embeddedAssets: '/embedded-assets',
	configuration: '/configuration/*',
	chat: '/chat',
	chatRedirect: '/chat-redirect',
	modeSelection: '/assets/:assetId/mode-selection',
	aiSegmentationProcessing: '/assets/:assetId/ai-segmentation-processing',
	aiVideoGenerationProcessing: '/assets/:assetId/ai-video-generation-processing',
	segmentation: '/assets/:assetId/segmentation',
	aiSearch: '/ai-search/*',
	aiSearchChat: '/ai-search/:id',

	// Learner desktop app routes
	learner: '/spotlight/*',
} as const;

type TRoutes = (typeof routes)[keyof typeof routes];

interface IItem {
	key: TMenuKeys;
	path: TRoutes;
	name: React.ReactNode;
	icon?: Icon;
	url?: string;
	component: (props: any) => React.ReactNode;
	children?: MenuDataItem[];
}

interface ISiderItem extends IItem {
	siderItem: true;
	dataTestId: string;
	allowedRoles: TAllRoles[];
}

interface INonSiderItem extends IItem {
	siderItem: false;
}

type IMenuItem = ISiderItem | INonSiderItem;

export const menuConfig: IMenuItem[] = [
	{
		key: 'Home',
		path: '/*',
		name: 'Home',
		icon: Home,
		component: HomePageWithAuth,
		siderItem: true,
		allowedRoles: [],
		dataTestId: 'home-menu-option',
	},
	{
		key: 'asset',
		path: '/content/:id/:searchTerm',
		name: 'asset',
		component: AssetPreviewPageWithAuth,
		siderItem: false,
	},
	{
		key: 'Assets',
		path: '/assets/*',
		name: 'Assets',
		icon: ViewGridList,
		component: AssetsPageWithAuth,
		siderItem: true,
		allowedRoles: [FEATURES_ROLES.allowViewAssets],
		dataTestId: 'assets-menu-option',
	},
	{
		key: 'Assignments',
		path: '/assignments/*',
		name: 'Assignments',
		icon: Notepad,
		component: AssignmentsPageWithAuth,
		siderItem: true,
		allowedRoles: [FEATURES_ROLES.accessManageAssignment],
		dataTestId: 'assignments-menu-option',
	},
	{
		key: 'Broadcasts',
		path: '/broadcasts/*',
		name: 'Broadcasts',
		component: BroadcastPageWithAuth,
		icon: Broadcast,
		siderItem: true,
		allowedRoles: [FEATURES_ROLES.accessBroadcasts],
		dataTestId: 'broadcasts-menu-option',
	},
	{
		key: 'User Lists',
		path: '/user-lists/*',
		name: 'User Lists',
		component: UserListPageWithAuth,
		icon: Peoples,
		siderItem: true,
		allowedRoles: [FEATURES_ROLES.accessUserList],
		dataTestId: 'user-lists-menu-option',
	},
	{
		key: 'Analytics',
		path: '/analytics/*',
		name: 'Analytics',
		icon: () => <AreaChartOutlined />,
		component: AnalyticsPageWithAuth,
		siderItem: true,
		allowedRoles: [FEATURES_ROLES.accessAnalytics],
		dataTestId: 'analytics-menu-option',
	},
	{
		key: 'Help',
		path: `https://help.okjeeves.app?idpHint=jeeves-${config.tenant}` as TRoutes,
		name: 'Help',
		icon: () => <ExportOutlined />,
		component: () => <>Redirecting to help</>,
		siderItem: true,
		allowedRoles: [],
		dataTestId: 'help-menu-option',
	},
	{
		key: 'AI Search',
		path: routes.aiSearch,
		name: 'AI Search',
		icon: ChatIcon,
		component: AiSearchPageWithAuth,
		siderItem: true,
		allowedRoles: [],
		dataTestId: 'ai-search-menu-option',
	},
	{
		key: 'Configuration',
		path: '/configuration/*',
		name: 'Configuration',
		icon: SettingTwo,
		component: () => <>Redirecting to Configuration</>,
		siderItem: true,
		allowedRoles: [FEATURES_ROLES.accessSettings],
		dataTestId: 'configuration-menu-option',
	},
	{
		key: 'System Status',
		path: '/system-status/*',
		name: 'System Status',
		component: SystemStatusPageWithAuth,
		siderItem: false,
	},
	{
		key: 'Edit',
		path: '/assets/:streamID/edit/*',
		name: 'Edit',
		component: EditPageWithAuth,
		siderItem: false,
	},
	{
		key: 'Mode Selection',
		path: '/assets/:assetId/mode-selection',
		name: 'Mode Selection',
		component: ModeSelectionPage,
		siderItem: false,
	},
	{
		key: 'AI Segmentation Processing',
		path: '/assets/:assetId/ai-segmentation-processing',
		name: 'AI Segmentation Processing',
		component: AISegmentationProcessingPage,
		siderItem: false,
	},
	{
		key: 'AI Video Generation Processing',
		path: '/assets/:assetId/ai-video-generation-processing',
		name: 'AI Video Generation Processing',
		component: AIVideoGenerationProcessingPage,
		siderItem: false,
	},
];

const iconProps: IIconProps = { theme: 'filled', size: 16 };

export const siderConfig: any = _.chain(
	menuConfig
		.filter((menu) => menu.siderItem)
		.map(({ icon: MenuIcon, ...menu }) => {
			return { ...menu, icon: MenuIcon ? <MenuIcon {...iconProps} fill='#595959' /> : undefined };
		}),
).value();

export const editMenuConfig: MenuDataItem[] = [
	{
		key: 'Transcript',
		path: '/assets/:streamID/edit/:alternateAssetType/transcript',
		tooltip: 'Transcript',
		name: 'Transcript',
		icon: <TranscriptSideBarIcon id='transcript-icon' />,
		dataTestId: 'transcript-menu-option',
	},
	{
		key: 'Interactive Elements',
		path: '/assets/:streamID/edit/:alternateAssetType/interactive-elements',
		tooltip: 'Interactive Elements',
		name: 'Interactive Elements',
		icon: <InteractiveSideBarIcon id='interactive-elements-icon' />,
		dataTestId: 'interactive-elements-menu-option',
	},
	{
		key: 'Non Interactive Elements',
		path: '/assets/:streamID/edit/:alternateAssetType/non-interactive-elements',
		tooltip: 'Non Interactive Elements',
		name: 'Non Interactive Elements',
		icon: <NonInteractiveSidebarIcon id='non-interactive-elements' />,
		dataTestId: 'non-interactive-elements-menu-option',
	},
	{
		key: 'Non Interactive Templates',
		path: '/assets/:streamID/edit/:alternateAssetType/non-interactive-templates',
		tooltip: 'Non Interactive Templates',
		name: 'Templates',
		icon: <Pic />,
		dataTestId: 'non-interactive-templates-menu-option',
	},
	{
		key: 'AI Voice Narration',
		path: '/assets/:streamID/edit/:alternateAssetType/audio',
		tooltip: 'Audio',
		name: 'Audio',
		icon: <MusicMenu id='ai-voice-narration-icon' />,
		dataTestId: 'ai-voice-narration-menu-option',
	},
	{
		key: 'Templates',
		path: '/assets/:streamID/edit/:alternateAssetType/templates',
		tooltip: 'Tip Sheet Templates',
		name: 'Templates',
		icon: <LayoutOne />,
		dataTestId: 'templates-menu-option',
	},
	{
		key: 'Review Comments',
		path: '/assets/:streamID/edit/:alternateAssetType/comments',
		tooltip: 'Comments',
		name: 'Comments',
		icon: <Comment id='review-comments-icon' />,
		dataTestId: 'review-comments-menu-option',
	},
	{
		key: 'Asset Referencing',
		path: '/assets/:streamID/edit/:alternateAssetType/asset-referencing',
		tooltip: 'Asset Referencing',
		name: 'Asset Referencing',
		icon: <LinkOutlined />,
		dataTestId: 'asset-referencing-menu-option',
	},
];

export const appRoutes = _.chain(routes)
	.values()
	.map((route) => _.split(route, '/')[1])
	.uniq()
	.filter((path) => !_.includes(['/', '*'], path))
	.value();

export const getEditSiderConfig = (
	editScreenParams: PathMatch<'streamID' | 'alternateAssetType'> | null,
	searchParams: URLSearchParams,
): MenuDataItem[] => {
	const queryString = searchParams.toString();

	return _.map(editMenuConfig, (editConfig) => {
		const basePath = generatePath(editConfig?.path ?? '', {
			streamID: editScreenParams?.params?.streamID ?? '',
			alternateAssetType: editScreenParams?.params?.alternateAssetType ?? '',
		});

		const fullPath = queryString ? `${basePath}?${queryString}` : basePath;

		return { ...editConfig, path: fullPath };
	});
};

export const isHomeScreenRoute = (location: Location<any>): boolean => {
	return (
		location.pathname === '/' ||
		_.some(
			[
				routes.myAssignments,
				routes.resources,
				routes.searchResults,
				routes.explore,
				routes.legacySearchAsset,
				routes.learnerAssetPreview,
				routes.aiSearch,
			],
			(path) => _.includes(location.pathname, _.replace(path, '/*', '')),
		)
	);
};
